import { State, Action, StateContext, Selector, NgxsOnInit, NgxsAfterBootstrap } from '@ngxs/store';
import { EditStateModel } from './edit.state.model';
import {
  SetView,
  ShowColumns,
  ShowColumn,
  ResetEditPage,
  NextView,
  PreviousView,
  SetLayerOrderMode,
  SetCanvasSize,
  ShowTreeNodeIds,
  AutoCapture
} from './edit.actions';
import { EditViewEnum } from '../models/edit-view.enum';
import { EditPaintState } from './paint/edit.paint.state';
import { ResetPaint } from './paint/edit.paint.actions';
import { CanvasSizeOriginal } from '../models/canvas-size.const';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { StorageService, LocalStorageEnum } from 'src/app/shared/storage.service';
import { Injectable } from '@angular/core';

const initialState: EditStateModel = {
  view: EditViewEnum.Chapter,
  showColumns: true,
  showLeftColumn: true,
  showRightColumn: true,
  showHeader: true,
  showFooter: true,
  autoCapture: (environment.application === EnvironmentApplication.Loft) ? false : true,
  record: true,
  layerOrderMode: false,
  showTreeNodeIds: false,
  canvasSize: CanvasSizeOriginal,
  canvasSettings: {
    emulateDrawer: true,
    emulateColumn: true
  }
};

@State<EditStateModel>({
  name: 'edit',
  defaults: initialState,
  children: [
    EditPaintState
  ]
})
@Injectable()
export class EditState implements NgxsOnInit {
  @Selector()
  static getView(state: EditStateModel) {
    return state.view;
  }

  @Selector()
  static getShowColumns(state: EditStateModel) {
    return state.showColumns;
  }

  @Selector()
  static getShowLeftColumn(state: EditStateModel) {
    return state.showLeftColumn;
  }

  @Selector()
  static getShowRightColumn(state: EditStateModel) {
    return state.showRightColumn;
  }

  @Selector()
  static getShowHeader(state: EditStateModel) {
    return state.showHeader;
  }

  @Selector()
  static getShowFooter(state: EditStateModel) {
    return state.showFooter;
  }

  @Selector()
  static getRecord(state: EditStateModel) {
    return state.record;
  }

  @Selector()
  static getLayerOrderMode(state: EditStateModel) {
    return state.layerOrderMode;
  }

  @Selector()
  static getShowTreeNodeIds(state: EditStateModel) {
    return state.showTreeNodeIds;
  }

  @Selector()
  static getCanvasSize(state: EditStateModel) {
    return state.canvasSize;
  }

  @Selector()
  static getCanvasSettings(state: EditStateModel) {
    return state.canvasSettings;
  }

  @Selector()
  static getAutoCapture(state: EditStateModel) {
    return state.autoCapture;
  }

  @Action(SetLayerOrderMode)
  SetLayerOrderMode(context: StateContext<EditStateModel>, action: SetLayerOrderMode) {
    context.patchState({
      layerOrderMode: action.layerOrderMode
    });
  }

  @Action(ShowTreeNodeIds)
  ShowTreeNodeIds(context: StateContext<EditStateModel>, action: ShowTreeNodeIds) {
    const show = (action.show != null) ? action.show : !context.getState().showTreeNodeIds;

    context.patchState({
      showTreeNodeIds: show
    });
  }

  @Action(SetView)
  setView(context: StateContext<EditStateModel>, action: SetView) {
    context.patchState({
      view: action.view
    });
  }

  @Action(NextView)
  nextView(context: StateContext<EditStateModel>, action: NextView) {
    const { view } = context.getState();

    const views = Object.values(EditViewEnum);
    const next = views[views.indexOf(view) + 1] || views[0];

    context.patchState({
      view: next
    });
  }

  @Action(PreviousView)
  previousView(context: StateContext<EditStateModel>, action: NextView) {
    const { view } = context.getState();

    const views = Object.values(EditViewEnum);
    const previous = views[views.indexOf(view) - 1] || views[views.length - 1];

    context.patchState({
      view: previous
    });
  }

  @Action(ShowColumns)
  showColumns(context: StateContext<EditStateModel>, action: ShowColumns) {
    context.patchState({
      showColumns: action.show
    });
  }

  @Action(ShowColumn)
  showColumn(context: StateContext<EditStateModel>, action: ShowColumn) {
    switch (action.column) {
      case 'left':
        context.patchState({
          showLeftColumn: action.show
        });
        break;
      case 'right':
        context.patchState({
          showRightColumn: action.show
        });
        break;
      case 'header':
        context.patchState({
          showHeader: action.show
        });
        break;
      case 'footer':
        context.patchState({
          showFooter: action.show
        });
        break;
    }
  }

  @Action(ResetEditPage)
  resetEditPage(context: StateContext<EditStateModel>, action: ResetEditPage) {
    context.setState(initialState);
    context.dispatch(new ResetPaint());
  }

  @Action(SetCanvasSize)
  setCanvasSize(context: StateContext<EditStateModel>, action: SetCanvasSize) {
    context.patchState({
      canvasSize: action.size
    });
  }

  @Action(AutoCapture)
  autoCapture(context: StateContext<EditStateModel>, action: AutoCapture) {
    context.patchState({
      autoCapture: action.enable
    });

    if (environment.application === EnvironmentApplication.Studio) {
      this.storageService.setLocalItem(LocalStorageEnum.AutoCapture, action.enable);
    }
  }

  constructor(
    private storageService: StorageService
  ) {}

  ngxsOnInit(context: StateContext<EditStateModel>) {
    setTimeout(() => {
      this.initAutoCapture(context);
    });
  }

  // For Studio users only, we persist their autoCapture setting between sessions
  // For Loft users, autoCapture always defaults to false
  private initAutoCapture(context: StateContext<EditStateModel>) {
    if (environment.application === EnvironmentApplication.Studio) {
      const autoCapture = this.storageService.getLocalJSON(LocalStorageEnum.AutoCapture);
      
      if (typeof autoCapture === 'boolean') {
        context.patchState({
          autoCapture: autoCapture
        });
      }
    }
  }
}
